import { useCallback, useState } from 'react';
import { ResultsTab } from './types';
import { SavedSearchDateRangeForm } from './saved-search';

type AddTabOptions<T> = {
    search: T;
    savedSearchId?: number;
    dateRangeOverride?: SavedSearchDateRangeForm;
};

export type TabsContextType<T> = {
    tabs: ResultsTab<T>[];
    currentTab: string | undefined;
    addTab: (options: AddTabOptions<T>) => void;
    setCurrentTab: (title: string) => void;
    deleteTab: (title: string) => void;
};

export function useSearchTabs<T>(initialTab?: string) {
    const [tabs, setTabs] = useState<ResultsTab<T>[]>([]);
    const [currentTab, setCurrentTab] = useState<string | undefined>(initialTab);

    const setSavedSearchId = useCallback(
        (tabId: number) => (savedSearchId?: number) => {
            setTabs((prevTabs) => {
                const matchingTabIndex = prevTabs.findIndex((tab) => tab.id === tabId);
                if (matchingTabIndex !== -1) {
                    const newTabs = [...prevTabs];
                    newTabs[matchingTabIndex].savedSearchId = savedSearchId;
                    return newTabs;
                }
                return prevTabs;
            });
        },
        []
    );

    const addTab = useCallback(
        ({ search, savedSearchId, dateRangeOverride }: AddTabOptions<T>) => {
            let title;
            setTabs((prevTabs) => {
                const maxTabId = Math.max(0, ...prevTabs.map((tab) => tab.id));
                const tabId = maxTabId + 1;
                title = `Results - ${tabId}`;
                return [
                    ...prevTabs,
                    {
                        id: tabId,
                        title,
                        search,
                        savedSearchId,
                        setSavedSearchId: setSavedSearchId(tabId),
                        dateRangeOverride,
                    },
                ];
            });
            setCurrentTab(title);
        },
        [setTabs, setSavedSearchId]
    );

    const deleteTab = useCallback(
        (title) => {
            setTabs((prevTabs) => prevTabs.filter((tab) => tab.title !== title));
            if (currentTab === title) {
                setCurrentTab(initialTab);
            }
        },
        [currentTab, initialTab]
    );

    return { tabs, currentTab, addTab, setCurrentTab, deleteTab };
}
